// src/components/layout/Masonry.tsx
import React, { useEffect, useLayoutEffect, useRef } from 'react';

type GridProps = React.HTMLAttributes<HTMLDivElement> & {
  /** hauteur “unitaire” des lignes du grid, en px (doit matcher le JS) */
  rowHeight?: number; // ex: 8
  /** gap vertical/horizontal en px (doit matcher tailwind gap-x/y si tu en utilises) */
  gap?: number; // ex: 24
};
export function MasonryGrid({
  className = '',
  rowHeight = 24,
  gap = 24,
  children,
  ...rest
}: GridProps) {
  return (
    <div
      {...rest}
      className={`grid md:grid-cols-2 lg:grid-cols-3 ${className}`}
      style={{
        gridAutoRows: `${rowHeight}px`,
        gridAutoFlow: 'dense',
         columnGap: `${gap}px`,  // espace horizontal
         rowGap: `12px`,     // espace vertical
 
      }}
    >
      {children}
    </div>
  );
}

type ItemProps = React.HTMLAttributes<HTMLDivElement> & {
  /** spans fixes optionnels (utiles pour forcer 2 colonnes etc.) */
  colSpan?: number;     // sm+
  colSpanMd?: number;   // md+
  colSpanLg?: number;   // lg+
  colSpanXl?: number;   // xl+
};

export function MasonryItem({
  className = '',
  colSpan,
  colSpanMd,
  colSpanLg,
  colSpanXl,
  children,
  ...rest
}: ItemProps) {
  const ref = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // calcule le nombre de lignes à “spanner”
  const recalc = () => {
  const node = ref.current;
  const content = contentRef.current;
  if (!node || !content) return;

  const grid = node.parentElement;
  if (!grid) return;

  const styles = window.getComputedStyle(grid);
  const rowHeight =
    parseFloat(styles.getPropertyValue('grid-auto-rows')) || 8;

  const rowGap =
    parseFloat(styles.getPropertyValue('row-gap')) ||
    parseFloat(styles.getPropertyValue('gap')) ||
    0;

  const contentHeight = content.getBoundingClientRect().height;

  // Correct formula: include the gap in both numerator and denominator
  const totalRow = rowHeight + rowGap;
  const span = Math.max(
    1,
    Math.ceil((contentHeight + rowGap + 0.5) / totalRow) // +epsilon
  );

  node.style.gridRowEnd = `span ${span}`;
};


  // recalcul au mount + resize + changement de contenu
  useLayoutEffect(() => {
    // 1) recalcul immédiat au mount
    recalc();

    // 2) Throttle via rAF pour éviter les boucles
    let frame: number | null = null;
    const schedule = () => {
      if (frame != null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        recalc();
      });
    };

    const ro = new ResizeObserver(() => {
      schedule();
    });

    if (contentRef.current) ro.observe(contentRef.current);

    const onResize = () => schedule();
    window.addEventListener('resize', onResize);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useLayoutEffect(() => {
  //   recalc();
  //     // 2) Throttle via rAF pour éviter les boucles
  //   let frame: number | null = null;
  //   const schedule = () => {
  //     if (frame != null) return;
  //     frame = requestAnimationFrame(() => {
  //       frame = null;
  //       recalc();
  //     });
  //   };

  //   const ro = new ResizeObserver(recalc);
  //   if (contentRef.current) ro.observe(contentRef.current);
  //   const onResize = () => recalc();
  //   window.addEventListener('resize', onResize);
  //   return () => {
  //     ro.disconnect();
  //     window.removeEventListener('resize', onResize);
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  // classes responsive de colSpan
  const spanClasses = [
    colSpan ? `sm:col-span-${colSpan}` : '',
    colSpanMd ? `md:col-span-${colSpanMd}` : '',
    colSpanLg ? `lg:col-span-${colSpanLg}` : '',
    colSpanXl ? `xl:col-span-${colSpanXl}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      {...rest}
      ref={ref}
      className={`${spanClasses} ${className}`}
      // IMPORTANT : aucune marge externe ici (laisser le GAP gérer l'espacement)
      style={{ breakInside: 'avoid' }}
    >
      {/* Contenant interne qui peut avoir padding/border */}
      <div ref={contentRef}>{children}</div>
    </div>
  );
}
